import Link from 'next/link';
import styled from 'styled-components';

import theme from '../assets/theme';

const clickerActions = [
  'Advance to the next slide',
  'Go back to the previous slide',
  'Open a passage or reading',
  'Start an activity or poll',
  'Stop an activity when the class is ready to move on',
];

const quickFacts = [
  'Works with any standard presentation clicker',
  'USB and Bluetooth supported',
  'Nothing new to install',
  'Works on every Curipod lesson, today',
];

const ClickerSupportPage = () => {
  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <HeroEyebrow>CLICKER SUPPORT IN CURIPOD</HeroEyebrow>
          <HeroTitle>
            Free yourself from the corner of your classroom and surround yourself with
            your students.
          </HeroTitle>
          <HeroLead>
            Curipod now works with presentation clickers. Run your lesson from anywhere in
            the room, so you can spend more time next to the students who need you.
          </HeroLead>
          <HeroActions>
            <Link href="/signup" passHref prefetch={false}>
              <PrimaryLink>Try it with your next lesson</PrimaryLink>
            </Link>
            <Link href="/quotes" passHref prefetch={false}>
              <SecondaryLink>Talk to us about the School Plan</SecondaryLink>
            </Link>
          </HeroActions>
        </HeroContent>
        <HeroCard aria-label="Curipod clicker support quick facts">
          {quickFacts.map(fact => (
            <TrustPill key={fact}>{fact}</TrustPill>
          ))}
        </HeroCard>
      </HeroSection>

      <InfoSection>
        <SectionHeader>
          <SectionEyebrow>More 1-on-1 time with students</SectionEyebrow>
          <SectionTitle>
            Proximity is one of the strongest tools for learning.
          </SectionTitle>
          <SectionLead>
            When teachers spend more time alongside individual students — checking
            understanding, giving quick feedback, redirecting attention — students
            participate more, retain more, and stay on task longer. Research on proximity,
            formative feedback, and student-teacher relationships consistently points the
            same direction: closer matters.
          </SectionLead>
          <SectionLead>
            A clicker keeps the lesson moving while you move. Less time at the laptop
            means more time conferring with a small group, sitting next to a student who
            is stuck, or quietly redirecting one who has drifted.
          </SectionLead>
        </SectionHeader>
      </InfoSection>

      <ActionsSection>
        <SectionHeader>
          <SectionEyebrow>What the clicker can do</SectionEyebrow>
          <SectionTitle>One clicker. Your whole lesson.</SectionTitle>
        </SectionHeader>
        <ActionsList>
          {clickerActions.map(action => (
            <ActionItem key={action}>{action}</ActionItem>
          ))}
        </ActionsList>
      </ActionsSection>

      <CompatibilitySection>
        <CompatibilityContent>
          <SectionEyebrow>Compatibility</SectionEyebrow>
          <DarkSectionTitle>Any clicker works.</DarkSectionTitle>
          <DarkSectionText>
            If a presentation clicker works with your slides, it works with Curipod. USB
            or Bluetooth, old or new, school-issued or the one in your supply closet. Plug
            it in, open your lesson, and walk.
          </DarkSectionText>
        </CompatibilityContent>
      </CompatibilitySection>

      <BottomCtaSection>
        <BottomCtaEyebrow>
          Curipod — designed to meet the modern classroom
        </BottomCtaEyebrow>
        <BottomCtaTitle>Try clicker support in your next Curipod lesson.</BottomCtaTitle>
        <CtaActions>
          <Link href="/signup" passHref prefetch={false}>
            <PrimaryLink>Try it with your next lesson</PrimaryLink>
          </Link>
          <Link href="/quotes" passHref prefetch={false}>
            <SecondaryLink>Talk to us about the School Plan</SecondaryLink>
          </Link>
        </CtaActions>
      </BottomCtaSection>
    </Container>
  );
};

const MOBILE_QUERY = '(max-width: 900px)';

const Container = styled.div`
  width: 100%;
  background: ${theme.colors.landingPageBeige};
  color: ${theme.colors.black};
`;

const HeroSection = styled.section`
  width: 100%;
  min-height: calc(100dvh - 60px);
  padding: 120px 24px 90px;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  gap: 56px;
  align-items: center;
  max-width: 1280px;
  margin: 0 auto;
  box-sizing: border-box;

  @media ${MOBILE_QUERY} {
    min-height: auto;
    grid-template-columns: 1fr;
    padding: 88px 20px 64px;
    gap: 32px;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const HeroEyebrow = styled.p`
  margin: 0 0 18px;
  color: ${theme.colors.green};
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 2.8px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  max-width: 860px;
  font-size: clamp(42px, 6vw, 72px);
  line-height: 1;
  font-weight: 900;
  letter-spacing: -0.05em;
`;

const HeroLead = styled.p`
  margin: 28px 0 0;
  max-width: 720px;
  font-size: 22px;
  line-height: 1.5;
  font-weight: 700;

  @media ${MOBILE_QUERY} {
    font-size: 18px;
  }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 36px;
`;

const BaseLink = styled.a`
  min-height: 52px;
  padding: 0 24px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 900;
  text-decoration: none;
  transition: ${theme.effects.defaultTransition};

  &:hover {
    transform: translateY(-2px);
  }
`;

const PrimaryLink = styled(BaseLink)`
  background: ${theme.colors.orange};
  color: ${theme.colors.black};
  box-shadow: 0 14px 28px rgba(249, 151, 70, 0.26);
`;

const SecondaryLink = styled(BaseLink)`
  border: 2px solid rgba(40, 47, 51, 0.18);
  color: ${theme.colors.black};
  background: rgba(255, 255, 255, 0.62);
`;

const HeroCard = styled.div`
  position: relative;
  border-radius: 32px;
  padding: 32px;
  background: linear-gradient(135deg, ${theme.colors.black} 0%, #1a1a2e 100%);
  box-shadow: 0 28px 70px rgba(40, 47, 51, 0.22);
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 240px;
    height: 240px;
    top: -80px;
    right: -60px;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.18);
  }
`;

const TrustPill = styled.div`
  position: relative;
  z-index: 1;
  padding: 18px 20px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  color: ${theme.colors.white};
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const InfoSection = styled.section`
  padding: 96px 24px;
  background: ${theme.colors.white};
`;

const SectionHeader = styled.div`
  max-width: 860px;
  margin: 0 auto 32px;
  text-align: center;
`;

const SectionEyebrow = styled.p`
  margin: 0 0 12px;
  color: ${theme.colors.green};
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 2.6px;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: clamp(32px, 4.5vw, 48px);
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const SectionLead = styled.p`
  margin: 20px auto 0;
  max-width: 760px;
  font-size: 19px;
  line-height: 1.6;
  font-weight: 600;
  color: rgba(40, 47, 51, 0.82);

  @media ${MOBILE_QUERY} {
    font-size: 17px;
  }
`;

const ActionsSection = styled.section`
  padding: 96px 24px;
  background: ${theme.colors.landingPageBeige};
`;

const ActionsList = styled.ul`
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionItem = styled.li`
  padding: 18px 22px;
  border-radius: 18px;
  background: ${theme.colors.white};
  border: 1px solid rgba(40, 47, 51, 0.08);
  box-shadow: 0 10px 24px rgba(40, 47, 51, 0.06);
  font-size: 18px;
  font-weight: 800;
  line-height: 1.35;
`;

const CompatibilitySection = styled.section`
  padding: 96px 24px;
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  display: flex;
  justify-content: center;
`;

const CompatibilityContent = styled.div`
  max-width: 760px;
  text-align: center;
`;

const DarkSectionTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.white};
  font-size: clamp(32px, 4.5vw, 48px);
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const DarkSectionText = styled.p`
  margin: 22px 0 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 19px;
  line-height: 1.6;
  font-weight: 600;
`;

const BottomCtaSection = styled.section`
  padding: 96px 24px;
  background: ${theme.colors.landingPageBeige};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const BottomCtaEyebrow = styled.p`
  margin: 0 0 16px;
  color: ${theme.colors.green};
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 2.6px;
  text-transform: uppercase;
`;

const BottomCtaTitle = styled.h2`
  margin: 0;
  max-width: 760px;
  font-size: clamp(32px, 4.5vw, 48px);
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: -0.04em;
`;

const CtaActions = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 32px;
`;

export default ClickerSupportPage;
